import React, { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { ChevronDown } from "lucide-react"

import getAppConfig from "~/utils/getAppConfig"
import { apiLineItems } from "~/routes"
import { formatCurrency } from "~/utils/helpers"

import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import CatchModal from "~/library/CatchModal"
import LoadingWrapper from "~/library/LoadingWrapper"
import Pagination from "~/library/Pagination"
import useFetchResource from "~/hooks/useFetchResource"
import { useToast } from "~/utils/Toast"
import NavigationLink from "~/library/NavigationLink"
import LineItem from "~/types/LineItem"
import BookingItem from "~/types/BookingItem"
import ModifierGroup from "~/types/ModifierGroup"
import type { BookingItemChange } from "~/types/BookingItemPayload"

import LineItemModifierControls from "./LineItemModifierControls"
import {
  bookingItemHasModifierChange,
  compactModifiers,
  lineItemSubtotalCents,
  modifierLimitsForQuantity,
  modifierQuantitiesFromBookingItems,
  ModifierQuantities,
} from "./lineItemModifierHelpers"

interface LineItemModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (changes: BookingItemChange[]) => void
  existingBookingItems: BookingItem[]
}

const LineItemModal: React.FC<LineItemModalProps> = ({
  isOpen,
  onClose,
  onSave,
  existingBookingItems,
}) => {
  const { venueSlug } = getAppConfig()
  const [selectedQuantities, setSelectedQuantities] = useState<{ [key: string]: number }>({})
  const [selectedModifiers, setSelectedModifiers] = useState<{
    [lineItemId: string]: ModifierQuantities
  }>({})
  const [expandedModifierItems, setExpandedModifierItems] = useState<{
    [lineItemId: string]: boolean
  }>({})
  const [pendingChanges, setPendingChanges] = useState<{ [key: string]: BookingItemChange }>({})
  const initializedRef = useRef(false)
  const toast = useToast()

  const scopeParams = {
    "q[active_eq]": "true",
  }

  const {
    data: availableLineItems,
    refetch,
    metadata,
    isLoading: isLineItemsLoading,
  } = useFetchResource<{ lineItems: LineItem[] }, "lineItems">(
    apiLineItems.index.path({ venueSlug }),
    "lineItems",
    { page: "1", ...scopeParams }
  )

  const handlePageChange = (page: string) => {
    refetch({ page: page, ...scopeParams })
  }

  // Initialize quantities when modal opens
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      const quantities = existingBookingItems.reduce<{ [key: string]: number }>((memo, item) => {
        memo[item.lineItemId] = item.quantity
        return memo
      }, {})
      const modifiers = modifierQuantitiesFromBookingItems(existingBookingItems)

      setSelectedQuantities(quantities)
      setSelectedModifiers(modifiers)
      initializedRef.current = true
    } else if (!isOpen) {
      initializedRef.current = false
      setPendingChanges({}) // Clear pending changes when modal closes
      setExpandedModifierItems({})
      setSelectedModifiers({})
    }
  }, [isOpen, existingBookingItems])

  const bookingLineItemSubtotalCents = (lineItem: LineItem) => {
    const quantity = selectedQuantities[lineItem.uuid] || lineItem.minOrderQty || 1
    return lineItemSubtotalCents(lineItem, quantity, selectedModifiers[lineItem.uuid] || {})
  }

  const toggleModifierItem = (lineItemId: string) => {
    setExpandedModifierItems((prev) => ({
      ...prev,
      [lineItemId]: !prev[lineItemId],
    }))
  }

  const handleQuantityChange = (lineItemData: LineItem, quantity: string) => {
    const lineItemId = lineItemData.uuid
    const minQuantity = lineItemData.minOrderQty || 1
    const parsedQuantity = parseInt(quantity, 10) || minQuantity
    const qty = Math.max(parsedQuantity, minQuantity)
    setSelectedQuantities((prev) => ({
      ...prev,
      [lineItemId]: qty,
    }))

    // Update pending changes if this item is already selected for changes
    const existingChange = pendingChanges[lineItemId]
    if (existingChange) {
      setPendingChanges((prev) => ({
        ...prev,
        [lineItemId]: {
          ...existingChange,
          quantity: qty,
          modifierIds: compactModifiers(selectedModifiers[lineItemId] || {}),
        },
      }))
    }

    if (parsedQuantity < minQuantity) {
      toast(
        `${lineItemData.name} quantity was set below the minimum order quantity of ${minQuantity}`,
        "warning"
      )
    }
  }

  const handleModifierQuantityChange = (
    lineItem: LineItem,
    group: ModifierGroup,
    modifierId: string,
    value: string
  ) => {
    const lineItemId = lineItem.uuid
    const requestedQuantity = Math.max(parseInt(value) || 0, 0)
    const currentModifiers = selectedModifiers[lineItemId] || {}

    const lineItemQuantity = selectedQuantities[lineItemId] || lineItem.minOrderQty || 1
    const { minAllowed, maxAllowed } = modifierLimitsForQuantity(group, lineItemQuantity)

    const selectedInOtherModifiers = group.modifiers.reduce((sum, modifier) => {
      if (modifier.id === modifierId) return sum
      return sum + (currentModifiers[modifier.id] || 0)
    }, 0)

    const availableQuantity =
      maxAllowed > 0 ? Math.max(maxAllowed - selectedInOtherModifiers, 0) : requestedQuantity

    const quantity = Math.min(requestedQuantity, availableQuantity)

    const nextModifiers = compactModifiers({
      ...currentModifiers,
      [modifierId]: quantity,
    })

    setSelectedModifiers((prev) => ({
      ...prev,
      [lineItemId]: nextModifiers,
    }))

    const selectedInGroup = selectedInOtherModifiers + quantity

    if (group.required && minAllowed > 0 && selectedInGroup > 0 && selectedInGroup < minAllowed) {
      toast(`${group.name} requires at least ${minAllowed} selections`, "warning")
    }

    const existingChange = pendingChanges[lineItemId]

    if (existingChange) {
      setPendingChanges((prev) => ({
        ...prev,
        [lineItemId]: {
          ...existingChange,
          modifierIds: nextModifiers,
        },
      }))
    }
  }

  const handleAddItem = (lineItem: LineItem) => {
    const lineItemId = lineItem.uuid
    const quantity = selectedQuantities[lineItemId] || 1
    const changeKey = lineItemId
    const existingBookingItem = existingBookingItems.find((item) => item.lineItemId === lineItemId)

    // Add to pending changes
    setPendingChanges((prev) => ({
      ...prev,
      [changeKey]: {
        ...(existingBookingItem && { id: existingBookingItem.id }), // Include ID for existing items
        lineItemId: lineItemId,
        name: lineItem.name,
        categoryName: lineItem.categoryName,
        quantity: quantity,
        amountCents: lineItem.amountCents,
        amountCurrency: lineItem.amountCurrency || "USD",
        modifierIds: compactModifiers(selectedModifiers[lineItemId] || {}),
        action: existingBookingItem ? "update" : "add",
      },
    }))
  }

  const handleSave = () => {
    const changes = Object.values(pendingChanges)
    onSave(changes)
    setPendingChanges({})
    onClose()
  }

  const isItemInBooking = (lineItemData: LineItem) => {
    const lineItemId = lineItemData.uuid
    return existingBookingItems.some((item) => item.lineItemId === lineItemId)
  }

  const hasModifierChange = (lineItemData: LineItem) => {
    const existingBookingItem = existingBookingItems.find(
      (item) => item.lineItemId === lineItemData.uuid
    )
    if (!existingBookingItem) return false

    return bookingItemHasModifierChange(
      existingBookingItem,
      selectedModifiers[lineItemData.uuid] || {}
    )
  }

  const renderLineItemName = (name: React.ReactNode, lineItem: LineItem) => {
    const hasModifiers = !!lineItem.modifierGroups?.length
    const expanded = !!expandedModifierItems[lineItem.uuid]

    return (
      <div className="booking-line-item__name">
        <NavigationLink type="line-items" to={`/line-items/${lineItem.id}`}>
          {name}
        </NavigationLink>
        {hasModifiers && (
          <button
            type="button"
            className={`booking-line-item__modifier-toggle ${expanded ? "is-expanded" : ""}`}
            title="Modifiers"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-title="Modifiers"
            aria-label={`${expanded ? "Hide" : "Show"} modifiers`}
            aria-expanded={expanded}
            onClick={() => toggleModifierItem(lineItem.uuid)}
          >
            <ChevronDown size={16} className="booking-line-item__modifier-toggle-icon" />
          </button>
        )}
      </div>
    )
  }

  const hasChanges = Object.keys(pendingChanges).length > 0

  return (
    <CatchModal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Line Items"
      size="xl"
      footer={
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Changes {hasChanges && `(${Object.keys(pendingChanges).length})`}
          </button>
        </div>
      }
    >
      <LoadingWrapper isLoading={isLineItemsLoading}>
        {Array.isArray(availableLineItems) && availableLineItems.length === 0 ? (
          <div className="text-center p-3">
            <p>No line items available.</p>
            <Link to="/line-items/new" className="btn btn-primary">
              Create Line Item
            </Link>
          </div>
        ) : (
          <CatchCard>
            <div className="mb-3">
              <small className="text-muted">
                Select line items to add to this booking. Change quantities and click
                &#34;Update&#34; for existing items.
              </small>
              {hasChanges && (
                <div className="alert alert-info mt-2 mb-0">
                  <small>
                    <strong>Selected:</strong> {Object.keys(pendingChanges).length} items will be
                    added
                  </small>
                </div>
              )}
            </div>
            <CatchTable
              data={Array.isArray(availableLineItems) ? availableLineItems : []}
              rowKey={(lineItem: LineItem) => lineItem.uuid}
              columns={[
                {
                  key: "name",
                  header: "Name",
                  formatter: (name, data: LineItem) => renderLineItemName(name, data),
                },
                {
                  key: "categoryName",
                  header: "Category",
                  formatter: (category: string) => category || "—",
                },
                {
                  key: "amountCents",
                  header: "Unit Price",
                  formatter: (amountCents: number) =>
                    formatCurrency({ cents: amountCents, currency_iso: "USD" }),
                },
                {
                  key: "minOrderQty",
                  header: "Min Order Qty",
                  formatter: (minOrderQty: number) => minOrderQty || "—",
                },
                {
                  key: "quantity",
                  header: "Quantity",
                  formatter: (_, data: LineItem) => (
                    <input
                      type="number"
                      min={data.minOrderQty || 1}
                      value={selectedQuantities[data.uuid] || data.minOrderQty || 1}
                      onChange={(e) => handleQuantityChange(data, e.target.value)}
                      className="booking-line-item__quantity-input"
                    />
                  ),
                },
                {
                  key: "subtotal",
                  header: "Subtotal",
                  formatter: (_, data: LineItem) => {
                    return formatCurrency({
                      cents: bookingLineItemSubtotalCents(data),
                      currency_iso: "USD",
                    })
                  },
                },
                {
                  key: "actions",
                  header: "Actions",
                  formatter: (_, data: LineItem) => {
                    const inBooking = isItemInBooking(data)
                    const pendingChange = pendingChanges[data.uuid]
                    const existingBookingItem = existingBookingItems.find(
                      (item) => item.lineItemId === data.uuid
                    )
                    const hasQuantityChange =
                      existingBookingItem &&
                      selectedQuantities[data.uuid] !== existingBookingItem.quantity
                    const hasSelectionChange = hasQuantityChange || hasModifierChange(data)

                    // Show update button if quantity changed for existing items
                    if (inBooking && hasSelectionChange && !pendingChange) {
                      return (
                        <button
                          type="button"
                          className="btn btn-warning btn-sm"
                          onClick={() => handleAddItem(data)}
                        >
                          Update
                        </button>
                      )
                    }

                    return (
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => handleAddItem(data)}
                        disabled={(inBooking && !hasSelectionChange) || !!pendingChange}
                      >
                        {pendingChange ? (
                          <span>&#x2713;</span>
                        ) : inBooking ? (
                          <span>&#x2713;</span>
                        ) : (
                          <span>+</span>
                        )}
                      </button>
                    )
                  },
                },
              ]}
              renderSubRow={(data: LineItem) =>
                data.modifierGroups?.length ? (
                  <LineItemModifierControls
                    expanded={!!expandedModifierItems[data.uuid]}
                    lineItem={data}
                    lineItemQuantity={selectedQuantities[data.uuid] || data.minOrderQty || 1}
                    onModifierQuantityChange={handleModifierQuantityChange}
                    selectedModifiers={selectedModifiers[data.uuid] || {}}
                  />
                ) : null
              }
            />
            {metadata && (
              <Pagination
                metadata={metadata}
                onPageChange={(page: number) => handlePageChange(page.toString())}
              />
            )}
          </CatchCard>
        )}
      </LoadingWrapper>
    </CatchModal>
  )
}

export default LineItemModal
